import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchMe, RBAC, type MeResponse } from "@/api";

/**
 * useEffectiveRole — single source of truth for the active role + permissions.
 *
 * Per ADR-20260427-role-based-view-overlay: staff users (token-authenticated
 * here, CF Access JWT later) can impersonate any client/staff role to preview
 * the experience. Impersonation lives in localStorage so it persists across
 * page navigation; URL params propagate to Worker via appendImpersonation().
 *
 * Returns:
 *   - me: full /api/me response
 *   - role: effective role (impersonated if active, else actual)
 *   - actualRole: the unimpersonated role (preserved for "View as Client" banner)
 *   - isImpersonating: boolean
 *   - hasPermission(key): convenience predicate
 *   - setImpersonation({role, tenant}): activate an overlay; refetches /api/me
 *   - clearImpersonation(): drop overlay
 */
export function useEffectiveRole() {
  const qc = useQueryClient();
  const meQ = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    staleTime: 30_000,
  });

  const me = meQ.data;

  const setImpersonation = useCallback(
    (params: { role: string; tenant?: string | null }) => {
      window.localStorage.setItem(RBAC.IMPERSONATE_ROLE_KEY, params.role);
      if (params.tenant) {
        window.localStorage.setItem(RBAC.IMPERSONATE_TENANT_KEY, params.tenant);
      } else {
        window.localStorage.removeItem(RBAC.IMPERSONATE_TENANT_KEY);
      }
      // Invalidate everything — every query reads through impersonation
      qc.invalidateQueries();
    },
    [qc]
  );

  const clearImpersonation = useCallback(() => {
    window.localStorage.removeItem(RBAC.IMPERSONATE_ROLE_KEY);
    window.localStorage.removeItem(RBAC.IMPERSONATE_TENANT_KEY);
    qc.invalidateQueries();
  }, [qc]);

  const hasPermission = useCallback(
    (key: string) => {
      if (!me?.permissions) return false;
      return me.permissions.includes(key);
    },
    [me]
  );

  return {
    me: (me as MeResponse | undefined) || null,
    role: me?.role || "anon",
    actualRole: me?.actual_role || "anon",
    scope: me?.scope || "",
    isImpersonating: me?.is_impersonating || false,
    isLoading: meQ.isLoading,
    hasPermission,
    setImpersonation,
    clearImpersonation,
  };
}
